import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Registration.css";
import logo from "../../assets/logo.webp";
import { assetUrl } from "../../utils/useContent";
import SEO from "../../Components/SEO/SEO";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5001";

export default function RegistrationPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/registration/forms`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => !cancelled && setForms(Array.isArray(data) ? data : []))
      .catch(() => !cancelled && setForms([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <SEO
        title="Register | All Star Xperience Montreal 7v7 Soccer"
        description="Register your team for upcoming ASX Montreal 7v7 soccer leagues and tournaments. Sign up now to compete for cash prizes and pro-shot photos."
        keywords="Register ASX, Montreal soccer registration, 7v7 team signup, ASX tournament registration"
      />
      <main className="reg-root">
        <section className="section container">
          <header className="reg-header">
            <h1 className="h1">
              Register to <span className="upcoming-league">Upcoming Events</span>
            </h1>
            <p className="p">Choose your event and submit a registration form.</p>
          </header>

          {loading ? null : forms.length === 0 ? (
            <div className="reg-empty">
              No registrations available right now. Check back soon for new ASX Events.
            </div>
          ) : (
            <ul className="reg-grid">
              {forms.map((f) => (
                <FormCard key={f.id} form={f} />
              ))}
            </ul>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}

function FormCard({ form }) {
  const regOpen = !!form.is_reg_open;
  const eventStatus = form.event_status || "upcoming";

  const eventTag =
    eventStatus === "ongoing" ? { label: "Ongoing Event", cls: "reg-tag-ongoing" }
    : eventStatus === "ended" ? { label: "Event Ended", cls: "reg-tag-ended" }
    : { label: "Upcoming Event", cls: "reg-tag-upcoming" };

  const showButton = regOpen || eventStatus === "upcoming";
  const btnLabel = regOpen ? "Start Registration" : "Submit Form";

  return (
    <li className="reg-card">
      <div className="reg-bn">
        {form.banner_image_path ? (
          <img
            src={assetUrl(form.banner_image_path)}
            alt=""
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="reg-bn-fallback">No banner</div>
        )}

        <div className={`reg-tag ${eventTag.cls}`}>{eventTag.label}</div>
        <div className={`reg-tag reg-tag-right ${regOpen ? "reg-tag-open" : "reg-tag-closed"}`}>
          {regOpen ? "Reg Open" : "Reg Closed"}
        </div>

        {!regOpen && (
          <div className="reg-overlay-closed">REGISTRATION CLOSED</div>
        )}
      </div>

      <div className="reg-body">
        <span className={`reg-pill ${regOpen ? "reg-pill-open" : "reg-pill-closed"}`}>
          {regOpen ? "Registration Open" : "Registration Closed"}
        </span>

        {form.league_label && <div className="reg-league">{form.league_label}</div>}
        <h3 className="reg-title">{form.title}</h3>

        <dl className="reg-meta">
          {(form.reg_start_date || form.reg_end_date) && (
            <>
              <dt>Registration</dt>
              <dd>
                {form.reg_start_date || ""}
                {form.reg_start_date && form.reg_end_date ? " – " : ""}
                {form.reg_end_date || ""}
              </dd>
            </>
          )}
          {(form.event_start_date || form.event_end_date) && (
            <>
              <dt>Event</dt>
              <dd>
                {form.event_start_date || ""}
                {form.event_start_date && form.event_end_date ? " – " : ""}
                {form.event_end_date || "TBA"}
              </dd>
            </>
          )}
        </dl>
      </div>

      {showButton && (
        <div className="reg-actions">
          <Link
            to={`/register/${form.slug}`}
            className={`reg-btn ${regOpen ? "reg-btn-primary" : ""}`}
          >
            {btnLabel}
          </Link>
        </div>
      )}
    </li>
  );
}

/* ---------- Footer ---------- */
function Footer() {
  const year = new Date().getFullYear();
  const links = [
    { label: "Home", href: "/" },
    { label: "Leagues", href: "/leagues" },
    { label: "Hall of Fame", href: "/hall-of-fame" },
    { label: "Rules", href: "/rules" },
    { label: "About", href: "/about" },
    { label: "Register", href: "/register" },
  ];

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="footer-top container">
        <div className="ft-col ft-brand">
          <div className="ft-logo">
            <img src={logo} alt="All Star Xperience" width="240" height="240"
              className="ft-logo-img" style={{ display: "block", objectFit: "contain" }} />
          </div>
        </div>
        <div className="ft-col" id="contact">
          <h4 className="ft-title">Contact</h4>
          <ul className="ft-contact">
            <li><span className="ft-k">Email</span><a href="mailto:allstarxperience@gmail.com">allstarxperience@gmail.com</a></li>
            <li><span className="ft-k">Phone</span><a href="tel:+14387283726">+1 (438) 728-3726</a></li>
            <li><span className="ft-k">Phone</span><a href="tel:+14385089880">+1 (438) 508-9880</a></li>
            <li><span className="ft-k">City</span><span>Montreal, QC</span></li>
          </ul>
        </div>
        <div className="ft-col explore-col">
          <h4 className="ft-title explore-title">Explore</h4>
          <ul className="ft-links">
            {links.map((l) => (<li key={l.label}><a href={l.href}>{l.label}</a></li>))}
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <span>© {year} All Star Xperience. All rights reserved. Developed by Riachi.</span>
        </div>
      </div>
    </footer>
  );
}
